import { PlusOutlined } from '@ant-design/icons';
import {
  ModalForm,
  PageContainer,
  ProFormSelect,
  ProFormText,
  ProTable,
} from '@ant-design/pro-components';
import { useMutation } from '@tanstack/react-query';
import { App, Button, Popconfirm, Tabs } from 'antd';
import { useRef } from 'react';
import { useOrganizationPath } from '@/common/hooks/use-organization-path';
import {
  cancelInvitation,
  createInvitation,
  queryInvitations,
  queryMembers,
  removeMember,
  updateMemberRole,
} from '../services/members';

const MEMBER_ROLE = {
  owner: {
    text: 'Owner',
    status: 'Success',
  },
  admin: {
    text: 'Admin',
    status: 'Processing',
  },
  member: {
    text: 'Member',
    status: 'Default',
  },
};

const INVITATION_STATUS = {
  pending: {
    text: '待处理',
    status: 'Processing',
  },
  rejected: {
    text: '已拒绝',
    status: 'Error',
  },
};

const roleOptions = [
  {
    label: 'Owner',
    value: 'owner',
  },
  {
    label: 'Admin',
    value: 'admin',
  },
  {
    label: 'Member',
    value: 'member',
  },
];

const MembersPage = () => {
  const memberActionRef = useRef(null);
  const invitationActionRef = useRef(null);
  const { message } = App.useApp();
  const { organizationId } = useOrganizationPath();

  const reload = () => {
    memberActionRef.current?.reload?.();
    invitationActionRef.current?.reload?.();
  };

  const { mutateAsync: invite, isPending: inviting } = useMutation({
    mutationFn: createInvitation,
    onSuccess: () => {
      message.success('邀请已发起');
      reload();
    },
    onError: () => {
      message.error('邀请发起失败，请重试');
    },
  });

  const { mutateAsync: changeRole, isPending: changingRole } = useMutation({
    mutationFn: updateMemberRole,
    onSuccess: () => {
      message.success('成员角色已更新');
      reload();
    },
    onError: () => {
      message.error('成员角色更新失败，请重试');
    },
  });

  const { mutateAsync: remove, isPending: removing } = useMutation({
    mutationFn: removeMember,
    onSuccess: () => {
      message.success('成员已移除');
      reload();
    },
    onError: () => {
      message.error('成员移除失败，请重试');
    },
  });

  const { mutateAsync: cancel, isPending: canceling } = useMutation({
    mutationFn: cancelInvitation,
    onSuccess: () => {
      message.success('邀请已取消');
      reload();
    },
    onError: () => {
      message.error('邀请取消失败，请重试');
    },
  });

  const memberColumns = [
    {
      title: '手机号',
      dataIndex: 'phoneNumber',
      copyable: true,
      renderText: (_, record) => record.user?.phoneNumber,
    },
    {
      title: '成员名称',
      dataIndex: 'name',
      renderText: (_, record) => record.user?.name,
    },
    {
      title: '角色',
      dataIndex: 'role',
      valueEnum: MEMBER_ROLE,
    },
    {
      title: '加入时间',
      dataIndex: 'createdAt',
      valueType: 'dateTime',
      search: false,
    },
    {
      title: '操作',
      valueType: 'option',
      render: (_, record) => [
        <ModalForm
          key="role"
          title="修改角色"
          trigger={<Button type="link">修改角色</Button>}
          width={480}
          initialValues={{ role: record.role }}
          modalProps={{
            destroyOnHidden: true,
            okButtonProps: {
              loading: changingRole,
            },
          }}
          onFinish={async (values) => {
            try {
              await changeRole({
                data: {
                  organizationId,
                  memberId: record.id,
                  role: values.role,
                },
              });
              return true;
            } catch {
              return false;
            }
          }}
        >
          <ProFormSelect
            name="role"
            label="角色"
            options={roleOptions}
            rules={[
              {
                required: true,
                message: '请选择成员角色',
              },
            ]}
          />
        </ModalForm>,
        <Popconfirm
          key="remove"
          title="移除成员"
          description={`确认移除 ${record.user?.phoneNumber || record.user?.name}？`}
          okText="确认"
          cancelText="取消"
          onConfirm={() =>
            remove({
              data: {
                organizationId,
                memberId: record.id,
              },
            })
          }
        >
          <Button type="link" danger loading={removing}>
            移除成员
          </Button>
        </Popconfirm>,
      ],
    },
  ];

  const invitationColumns = [
    {
      title: '手机号',
      dataIndex: 'phoneNumber',
      copyable: true,
    },
    {
      title: '角色',
      dataIndex: 'role',
      valueEnum: MEMBER_ROLE,
    },
    {
      title: '邀请状态',
      dataIndex: 'status',
      valueEnum: INVITATION_STATUS,
    },
    {
      title: '拒绝时间',
      dataIndex: 'rejectedAt',
      valueType: 'dateTime',
      search: false,
      render: (dom, record) => (record.status === 'rejected' && record.rejectedAt ? dom : '-'),
    },
    {
      title: '过期时间',
      dataIndex: 'expiresAt',
      valueType: 'dateTime',
      search: false,
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      valueType: 'dateTime',
      search: false,
    },
    {
      title: '操作',
      valueType: 'option',
      render: (_, record) => {
        if (record.status === 'rejected') {
          return [
            <Button
              key="reinvite"
              type="link"
              loading={inviting}
              onClick={() =>
                invite({
                  data: {
                    organizationId,
                    phoneNumber: record.phoneNumber,
                    role: record.role,
                  },
                })
              }
            >
              重新邀请
            </Button>,
          ];
        }

        return [
          <Popconfirm
            key="cancel"
            title="取消邀请"
            description={`确认取消 ${record.phoneNumber} 的邀请？`}
            okText="确认"
            cancelText="取消"
            onConfirm={() =>
              cancel({
                data: {
                  organizationId,
                  invitationId: record.id,
                },
              })
            }
          >
            <Button type="link" danger loading={canceling}>
              取消邀请
            </Button>
          </Popconfirm>,
        ];
      },
    },
  ];

  return (
    <PageContainer content="组织管理员在这里管理已加入成员和未加入成员。">
      <Tabs
        items={[
          {
            key: 'members',
            label: '已加入成员',
            children: (
              <ProTable
                actionRef={memberActionRef}
                rowKey="id"
                headerTitle="已加入成员"
                request={(params) => queryMembers({ ...params, organizationId })}
                columns={memberColumns}
                search={{
                  labelWidth: 96,
                }}
                scroll={{
                  x: 'max-content',
                }}
              />
            ),
          },
          {
            key: 'invitations',
            label: '未加入成员',
            children: (
              <ProTable
                actionRef={invitationActionRef}
                rowKey="id"
                headerTitle="未加入成员"
                request={(params) => queryInvitations({ ...params, organizationId })}
                columns={invitationColumns}
                search={{
                  labelWidth: 96,
                }}
                scroll={{
                  x: 'max-content',
                }}
                toolBarRender={() => [
                  <ModalForm
                    key="invite"
                    title="发起邀请"
                    trigger={
                      <Button type="primary" icon={<PlusOutlined />}>
                        发起邀请
                      </Button>
                    }
                    width={480}
                    modalProps={{
                      destroyOnHidden: true,
                      okButtonProps: {
                        loading: inviting,
                      },
                    }}
                    onFinish={async (values) => {
                      try {
                        await invite({
                          data: {
                            organizationId,
                            ...values,
                          },
                        });
                        return true;
                      } catch {
                        return false;
                      }
                    }}
                  >
                    <ProFormText
                      name="phoneNumber"
                      label="手机号"
                      placeholder="请输入手机号"
                      rules={[
                        {
                          required: true,
                          message: '请输入手机号',
                        },
                        {
                          pattern: /^1\d{10}$/,
                          message: '请输入正确的手机号',
                        },
                      ]}
                    />
                    <ProFormSelect
                      name="role"
                      label="角色"
                      options={roleOptions}
                      initialValue="member"
                      rules={[
                        {
                          required: true,
                          message: '请选择成员角色',
                        },
                      ]}
                    />
                  </ModalForm>,
                ]}
              />
            ),
          },
        ]}
      />
    </PageContainer>
  );
};

export default MembersPage;
